import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Route, RouteProps } from '@modernhealth/web-analytics';

import { Dispatch, RootState } from 'src/config/rootStore';
import * as authSelectors from 'src/modules/auth/selectors/authenticationSelectors';
import { isUserDataFetched } from 'src/modules/user/selectors/userSelectors';
import { CenteredSpinner } from 'src/shared/components/CenteredSpinner';

interface Props
    extends RouteProps,
        ReturnType<typeof mapStateToProps>,
        ReturnType<typeof mapDispatchToProps> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component: React.ComponentType<any>;
    specialView?: boolean;
    secured?: boolean;
    redirectTo?: string;
    additionalProps?: Record<string, unknown>;
}

const RouteGuardContainer: React.FC<Props> = ({
    component: Component,
    isAuthenticated,
    isAuthenticating,
    shouldValidateAuthData,
    validateAuthData,
    userDataFetched,
    justSignedOut,
    finishSignOut,
    specialView = false,
    secured = false,
    redirectTo = '',
    additionalProps = {},
    ...routeProps
}) => {
    // will be true when auth data loaded from the localstorage after site reload
    useEffect(() => {
        if (shouldValidateAuthData && !isAuthenticating) {
            validateAuthData();
        }
    }, []);

    if (isAuthenticating) {
        return <CenteredSpinner />;
    }

    if (justSignedOut) {
        finishSignOut();

        return <Redirect to="/login" />;
    }

    if (secured) {
        if (!isAuthenticated) {
            localStorage.setItem('requestedUrl', window.location.pathname);
            localStorage.setItem('requestedUrlParams', window.location.search);
        } else if (!userDataFetched) {
            if (!localStorage.getItem('requestedUrl')) {
                localStorage.setItem('requestedUrl', window.location.pathname);
                localStorage.setItem('requestedUrlParams', window.location.search);
            }

            return <Redirect to="/authenticated" />;
        }
    }

    if (!redirectTo) {
        redirectTo = secured ? '/' : '/dashboard';
    }

    if (!(isAuthenticated === secured || specialView)) {
        return <Redirect to={redirectTo} />;
    }

    return (
        <Route {...routeProps} render={props => <Component {...props} {...additionalProps} />} />
    );
};

const mapStateToProps = (state: RootState) => ({
    isAuthenticated: authSelectors.isAuthenticated(state),
    justSignedOut: authSelectors.getJustSignedOut(state),
    authenticating: authSelectors.isAuthenticating(state),
    shouldValidateAuthData: authSelectors.shouldValidateAuthData(state),
    isAuthenticating: authSelectors.isAuthenticating(state),
    userDataFetched: isUserDataFetched(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    validateAuthData: dispatch.authentication.validateAuthData,
    finishSignOut: dispatch.authentication.finishSignOut,
});

export const RouteGuard = connect(mapStateToProps, mapDispatchToProps)(RouteGuardContainer);
